import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { ApiError } from '@/lib/apiClient'
import { cn } from '@/lib/cn'
import { minorUnits } from '@/lib/money'

import type { FloorOrder, StationState } from '../types'

/**
 * Une commande, une carte — la même à table, en cuisine et au bar.
 *
 * Tenue d'une main dans une salle sombre et bruyante : de gros chiffres, une
 * action principale par carte, et rien à calculer de tête. Le dû, le reçu et
 * la monnaie viennent du serveur ; le temps écoulé aussi.
 *
 * `station` nul : la carte du serveur (tous les postes, son état par poste).
 * Sinon : la carte d'un poste, avec ses seules lignes et son chrono.
 */
export function OrderCard({
  order,
  station,
  money,
  busy,
  onSettle,
  onCancel,
  onDone,
}: {
  order: FloorOrder
  station: 'kitchen' | 'bar' | null
  money: (minor: number) => string
  busy: boolean
  onSettle?:
    | ((payload: { received_minor: number | null; method: 'cash' | 'paypal' }) => void)
    | undefined
  onCancel?: ((reason: string) => void) | undefined
  onDone?: (() => void) | undefined
}) {
  const { t } = useTranslation()
  const [mode, setMode] = useState<'idle' | 'settle' | 'cancel'>('idle')

  const unpaid = order.status === 'pending_payment'
  const cancelled = order.status === 'cancelled' || order.status === 'refunded'
  const state = station ? order.station_state : null
  const where = useWhere()(order)
  const late = !cancelled && state !== 'done' && order.elapsed_minutes >= 20
  const warning = !cancelled && state !== 'done' && order.elapsed_minutes >= 10

  return (
    <li
      className={cn(
        'flex min-w-0 flex-col gap-3 rounded-lg border bg-bg-elevated p-4',
        late
          ? 'border-2 border-state-danger'
          : warning
            ? 'border-ember-400'
            : unpaid
              ? 'border-champagne-400/50'
              : 'border-line-subtle',
        cancelled && 'opacity-70',
      )}
    >
      {/* La table d'abord : c'est par elle qu'on cherche une commande. Sans
          table, le prénom — et qui la remet : le serveur, ou le comptoir. */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="break-words font-display text-heading-md leading-tight text-content-primary">
            {where.title}
          </p>
          {where.detail ? (
            <p className="mt-0.5 break-words text-sm text-champagne-300">{where.detail}</p>
          ) : null}
          <p className="mt-0.5 truncate text-caption text-content-muted">
            <span className="font-mono">{order.order_number}</span>
            {/* « Sans serveur » n'a de sens que pour une table : une vente du
                comptoir, ou la commande d'un invité sans table, n'est à aucun
                serveur par nature — c'est le poste qui la remet. */}
            {order.waiter_name
              ? ` · ${order.waiter_name}`
              : order.table_label
                ? ` · ${t('service.noWaiter')}`
                : ''}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p
            className={cn(
              'font-mono text-heading-sm tabular-nums',
              late ? 'text-state-danger' : warning ? 'text-ember-300' : 'text-content-muted',
            )}
            title={t('service.elapsedHint')}
          >
            {order.elapsed_minutes}′
          </p>
          <StatusBadge order={order} state={state} />
        </div>
      </div>

      <ul className="space-y-1">
        {order.lines.map((line) => (
          <li key={line.id} className="flex items-baseline justify-between gap-3">
            <span
              className={cn(
                'min-w-0 text-[clamp(1.05rem,2.6vw,1.35rem)] leading-snug text-content-primary',
                line.prep_status === 'cancelled' && 'line-through opacity-60',
              )}
            >
              <span className="text-champagne-300">{line.quantity}×</span> {line.label}
              {/* Les bouteilles de CETTE table, telles que sa réservation
                  les porte — modifiées pour elle si l'équipe l'a ajustée. */}
              {line.details && line.details.length > 0 ? (
                <span className="mt-0.5 block text-sm text-content-secondary">
                  {line.details.join(' · ')}
                </span>
              ) : null}
            </span>
            {station ? null : (
              <span className="shrink-0 text-caption text-content-muted">
                {t(`service.stationShort.${line.station}`, { defaultValue: line.station })}
              </span>
            )}
          </li>
        ))}
      </ul>

      {/* Le serveur suit chaque poste : la cuisine a fini, le bar non. */}
      {station === null && Object.keys(order.stations).length > 0 && !unpaid && !cancelled ? (
        <div className="flex flex-wrap gap-2">
          {Object.entries(order.stations).map(([name, value]) => (
            <span
              key={name}
              className={cn(
                'rounded-full border px-3 py-1 text-caption',
                value === 'done'
                  ? 'border-state-success/40 text-state-success'
                  : 'border-ember-400/40 text-ember-300',
              )}
            >
              {t(`service.stationShort.${name}`)} · {t(`service.state.${value}`)}
            </span>
          ))}
        </div>
      ) : null}

      {order.note ? (
        <p className="rounded-md bg-bg-overlay px-3 py-2 text-sm text-content-primary">
          {order.note}
        </p>
      ) : null}

      <dl className="space-y-1 border-t border-line-subtle pt-2 text-sm">
        <Row label={t('service.due')} value={money(order.total_minor)} strong />
        {station && order.station_total_minor !== null && order.station_total_minor !== order.total_minor ? (
          <Row label={t('service.stationPart')} value={money(order.station_total_minor)} />
        ) : null}
        <Row
          label={t('service.method')}
          value={
            order.payment_method
              ? t(`service.methods.${order.payment_method}`, {
                  defaultValue: order.payment_method,
                })
              : '—'
          }
        />
        {order.received_minor !== null && !unpaid ? (
          <>
            <Row label={t('service.received')} value={money(order.received_minor)} />
            <Row label={t('service.changeGiven')} value={money(order.change_minor ?? 0)} />
          </>
        ) : null}
        {!unpaid && order.collected_by_name ? (
          <Row
            label={t('service.collectedBy')}
            value={
              order.register_label
                ? `${order.collected_by_name} · ${order.register_label}`
                : order.collected_by_name
            }
          />
        ) : null}
        {unpaid && order.payment_method === 'paypal' && order.payment_reference ? (
          <Row label={t('service.reference')} value={order.payment_reference} />
        ) : null}
        {order.refunded_minor > 0 ? (
          <Row label={t('service.refunded')} value={money(order.refunded_minor)} />
        ) : null}
      </dl>

      {cancelled && order.cancel_reason ? (
        <p className="text-sm text-ember-300">
          {t('service.cancelledBecause', { reason: order.cancel_reason })}
        </p>
      ) : null}

      {cancelled ? null : mode === 'settle' && onSettle ? (
        <SettleForm
          order={order}
          busy={busy}
          onSubmit={(payload) => onSettle(payload)}
          onBack={() => setMode('idle')}
        />
      ) : mode === 'cancel' && onCancel ? (
        <CancelForm
          paid={!unpaid}
          busy={busy}
          onSubmit={(reason) => onCancel(reason)}
          onBack={() => setMode('idle')}
        />
      ) : (
        <div className="flex flex-wrap gap-2">
          {unpaid && onSettle ? (
            <Button size="lg" className="flex-1" disabled={busy} onClick={() => setMode('settle')}>
              {t('service.collect')}
            </Button>
          ) : null}
          {!unpaid && onDone && state === 'waiting' ? (
            <Button size="lg" className="flex-1" disabled={busy} onClick={onDone}>
              {t('service.done')}
            </Button>
          ) : null}
          {onCancel ? (
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setMode('cancel')}>
              {t('service.cancel')}
            </Button>
          ) : null}
        </div>
      )}
    </li>
  )
}

/**
 * Où va une commande, tel que la salle et les postes le lisent.
 *
 * * à une table : « T12 · Champagne » (et « à retirer au comptoir » si
 *   l'invité vient la chercher) ;
 * * sans table : « Sans table · Paul », puis le repère et qui la remet —
 *   « (près de la piste) · servi par Marc », ou « à retirer au comptoir » ;
 * * au comptoir : « Comptoir · Paul », le prénom à appeler s'il a été donné.
 */
export function useWhere() {
  const { t } = useTranslation()
  return (order: FloorOrder): { title: string; detail: string | null } => {
    const pickup = order.service_mode === 'counter_pickup' ? t('service.where.pickup') : null
    if (order.table_label) return { title: order.table_label, detail: pickup }
    const name = order.customer_label
    if (order.service_mode === null) {
      return { title: [t('service.counter'), name].filter(Boolean).join(' · '), detail: null }
    }
    const waiter = (order.waiter_name ?? '').split(' ')[0]
    const by = pickup ?? (waiter ? t('service.where.servedBy', { name: waiter }) : null)
    return {
      title: [t('service.where.noTable'), name].filter(Boolean).join(' · '),
      detail: [order.spot_note ? `(${order.spot_note})` : null, by].filter(Boolean).join(' · ') || null,
    }
  }
}

function StatusBadge({ order, state }: { order: FloorOrder; state: StationState | null }) {
  const { t } = useTranslation()
  const value: StationState =
    state ??
    (order.status === 'pending_payment'
      ? 'unpaid'
      : order.status === 'cancelled' || order.status === 'refunded'
        ? 'cancelled'
        : Object.values(order.stations).every((each) => each === 'done')
          ? 'done'
          : 'waiting')
  return (
    <span
      className={cn(
        'mt-1 inline-block rounded-full border px-2.5 py-0.5 text-caption uppercase',
        value === 'unpaid' && 'border-champagne-400/50 text-champagne-300',
        value === 'waiting' && 'border-ember-400/40 text-ember-300',
        value === 'done' && 'border-state-success/40 text-state-success',
        value === 'cancelled' && 'border-line text-content-muted',
      )}
    >
      {t(`service.state.${value}`)}
    </span>
  )
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-content-secondary">{label}</dt>
      <dd
        className={cn(
          'tabular-nums',
          strong ? 'font-display text-heading-sm text-content-primary' : 'text-content-primary',
        )}
      >
        {value}
      </dd>
    </div>
  )
}

/**
 * Encaisser : le moyen réel, et — en espèces — ce qu'on a **reçu**.
 *
 * La monnaie n'est pas calculée ici : le serveur la rend après l'encaissement,
 * et c'est ce chiffre-là qui s'affiche en grand.
 */
function SettleForm({
  order,
  busy,
  onSubmit,
  onBack,
}: {
  order: FloorOrder
  busy: boolean
  onSubmit: (payload: { received_minor: number | null; method: 'cash' | 'paypal' }) => void
  onBack: () => void
}) {
  const { t } = useTranslation()
  const [method, setMethod] = useState<'cash' | 'paypal'>(
    order.payment_method === 'cash' ? 'cash' : 'paypal',
  )
  const [received, setReceived] = useState('')
  const digits = minorUnits(order.currency)
  const value = Number.parseFloat(received.replace(',', '.'))
  const receivedMinor =
    Number.isFinite(value) && value >= 0 ? Math.round(value * 10 ** digits) : null

  return (
    <div className="space-y-3 rounded-md border border-line-subtle bg-bg-base p-3">
      <div className="flex gap-2">
        {(['cash', 'paypal'] as const).map((option) => (
          <Button
            key={option}
            size="sm"
            variant={method === option ? 'ember' : 'outline'}
            className="flex-1"
            onClick={() => setMethod(option)}
          >
            {t(`service.methods.${option}`)}
          </Button>
        ))}
      </div>
      {method === 'cash' ? (
        <label className="block text-caption text-content-muted">
          {t('service.receivedLabel')}
          <div className="mt-1 flex gap-2">
            <input
              inputMode="decimal"
              value={received}
              autoFocus
              onChange={(event) => setReceived(event.target.value)}
              className="h-12 min-w-0 flex-1 rounded-md border border-line bg-bg-elevated px-3 text-lg text-content-primary"
            />
            <Button
              variant="outline"
              size="md"
              onClick={() => setReceived((order.total_minor / 10 ** digits).toFixed(digits))}
            >
              {t('service.exact')}
            </Button>
          </div>
          <span className="mt-1 block">{t('service.receivedHint')}</span>
        </label>
      ) : (
        <p className="text-caption text-content-muted">{t('service.paypalHint')}</p>
      )}
      {/* Empilés sur téléphone : côte à côte, ces deux gros boutons
          élargissaient la fiche au-delà de l'écran — et le montant dû,
          aligné à droite, sortait de la vue. */}
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          size="lg"
          className="w-full sm:flex-1"
          disabled={busy || (method === 'cash' && receivedMinor === null)}
          onClick={() =>
            onSubmit({ received_minor: method === 'cash' ? receivedMinor : null, method })
          }
        >
          {method === 'cash' ? t('service.confirmCash') : t('service.confirmPaypal')}
        </Button>
        <Button variant="ghost" size="lg" className="w-full sm:w-auto" onClick={onBack}>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  )
}

function CancelForm({
  paid,
  busy,
  onSubmit,
  onBack,
}: {
  paid: boolean
  busy: boolean
  onSubmit: (reason: string) => void
  onBack: () => void
}) {
  const { t } = useTranslation()
  const [reason, setReason] = useState('')
  return (
    <div className="space-y-3 rounded-md border border-state-danger/40 bg-bg-base p-3">
      <p className="text-sm text-content-secondary">
        {paid ? t('service.cancelPaidHint') : t('service.cancelUnpaidHint')}
      </p>
      <input
        value={reason}
        autoFocus
        onChange={(event) => setReason(event.target.value)}
        placeholder={t('service.cancelReason')}
        className="h-12 w-full rounded-md border border-line bg-bg-elevated px-3 text-content-primary"
      />
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          variant="outline"
          size="lg"
          className="w-full sm:flex-1"
          disabled={busy || reason.trim().length < 2}
          onClick={() => onSubmit(reason.trim())}
        >
          {t('service.confirmCancel')}
        </Button>
        <Button variant="ghost" size="lg" className="w-full sm:w-auto" onClick={onBack}>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  )
}

/** Le message d'une erreur d'API, traduit quand on en connaît le code. */
export function useErrorText() {
  const { t } = useTranslation()
  return (error: unknown): string | null => {
    if (!error) return null
    if (error instanceof ApiError) {
      return t(`service.error.${error.code}`, { defaultValue: error.message }) as string
    }
    return t('state.errorTitle')
  }
}

/**
 * La monnaie à rendre, en grand, juste après l'encaissement.
 *
 * C'est le chiffre qu'on lit à voix haute en rendant — se tromper là coûte de
 * l'argent à chaque fois. Il vient du serveur.
 */
export function ChangeBanner({
  orderNumber,
  table,
  stations,
  change,
  register,
  money,
  onDismiss,
}: {
  orderNumber: string
  table: string | null
  /** Les postes qui reçoivent la commande : `FloorOrder.stations`. */
  stations: Record<string, StationState>
  change: number
  register: string | null
  money: (minor: number) => string
  onDismiss: () => void
}) {
  const { t } = useTranslation()
  // Dire exactement où la commande est partie : « en cuisine et au bar »
  // s'affichait aussi pour trois bières, et le serveur allait attendre un
  // plat qui n'existait pas.
  const kitchen = 'kitchen' in stations
  const bar = 'bar' in stations
  const where = kitchen && bar ? 'both' : kitchen ? 'kitchen' : bar ? 'bar' : 'none'
  return (
    <div
      role="status"
      className="rounded-lg border-2 border-state-success bg-state-success/5 p-4"
    >
      <p className="text-sm text-content-secondary">
        {t(`service.settledWhere.${where}`, { order: orderNumber, table: table ?? '—' })}
      </p>
      <p className="mt-2 overline text-content-muted">{t('service.giveBack')}</p>
      <p className="font-display text-display-sm tabular-nums text-state-success">
        {money(change)}
      </p>
      <p className="mt-2 text-caption text-content-muted">
        {register ? t('service.inRegister', { register }) : t('service.inPocket')}
      </p>
      <Button variant="ghost" size="sm" className="mt-2" onClick={onDismiss}>
        {t('service.ok')}
      </Button>
    </div>
  )
}
